/**
 * NL-041 package adviser, bound to Funnel Planner context.
 *
 * This is a thin, pure adapter over the shared `resolvePackageRecommendation`
 * rule so there is exactly one tier implementation in the codebase. It adds the
 * planner's own concerns and nothing else:
 *
 * - translating planner state (audience, template, products, region) into the
 *   shared recommender's input vocabulary;
 * - deciding whether the caller-owned pricing snapshot may be shown at all;
 * - recording the visitor's accept / override / skip decision.
 *
 * Everything it produces is advisory. A pricing band is an indicative range for
 * orientation, never a quote, an invoice, a commitment, or a revenue figure, and
 * there is no representation in this module for any of those. Incomplete pricing
 * context fails closed to `manualQuoteRequired`, which the NL-014 receipt
 * builder then refuses to record as a consumed input.
 *
 * Pure: no clock, no I/O, no environment reads, no state mutation.
 */
import {
  PACKAGE_RECOMMENDATION_TIERS,
  resolvePackageRecommendation,
  type PackageComplexitySignal,
  type PackageRecommendation as SharedPackageRecommendation,
} from "../packages/package-recommender";
import {
  DEFAULT_PACKAGE_RECOMMENDATION,
  FUNNEL_PACKAGE_TIERS,
  type FunnelPackageTier,
  type FunnelTemplateId,
  type PackageAdviserDecision,
  type PackageRecommendation as PlannerPackageRecommendationState,
  type Product,
  type RegionalSettings,
} from "./schema";

/**
 * A caller-owned, display-ready pricing snapshot. The planner never derives,
 * converts, or invents a price; it only decides whether an already-approved
 * snapshot is applicable to the visitor's stated currency.
 */
export type PlannerPricingSnapshot = {
  readonly version: string;
  /** The currency the band strings are denominated in. */
  readonly currency: string;
  readonly bands: Readonly<Record<FunnelPackageTier, string>>;
  /** True when the band was converted from the canonical currency at an FX rate. */
  readonly approximate?: boolean;
};

export type PackageAdviserInput = {
  readonly audience: "agency" | "franchise" | "creator" | "adult" | "enterprise" | null;
  readonly templateId: FunnelTemplateId | null;
  readonly products: readonly Product[];
  readonly regionalSettings: Pick<RegionalSettings, "region" | "currency">;
  readonly pricingSnapshot?: PlannerPricingSnapshot | null;
  readonly decision: PackageAdviserDecision;
  readonly overrideTier?: FunnelPackageTier | null;
};

/** Why an indicative band was withheld. Reported, never silently swallowed. */
export type PricingContextBlocker =
  | "no-pricing-snapshot"
  | "currency-mismatch"
  | "missing-region"
  | "missing-currency";

export type PlannerPackageAdvice = {
  readonly ruleVersion: SharedPackageRecommendation["ruleVersion"];
  readonly recommendedTier: FunnelPackageTier;
  readonly selectedTier: FunnelPackageTier | null;
  readonly decision: PackageAdviserDecision;
  readonly reasons: SharedPackageRecommendation["reasons"];
  readonly pricingBand: string | null;
  readonly pricingSnapshotVersion: string | null;
  readonly pricingCurrency: string | null;
  readonly pricingContextBlockers: readonly PricingContextBlocker[];
  readonly missingInformation: SharedPackageRecommendation["missingInformation"];
  /** True whenever the band is unavailable or the tier needs human scoping. */
  readonly manualQuoteRequired: boolean;
};

/**
 * Templates that describe an inherently multi-brand or regulated engagement.
 * These mirror the shared recommender's own signal vocabulary rather than
 * introducing planner-specific scoring.
 */
const TEMPLATE_COMPLEXITY_SIGNALS: Readonly<Partial<Record<FunnelTemplateId, PackageComplexitySignal>>> = {
  "agency-white-label": "white-label-service",
  "franchise-multi-location": "franchise-local-service",
  "adult-compliance": "compliance-addon",
};

function isFunnelPackageTier(value: unknown): value is FunnelPackageTier {
  return typeof value === "string" && (FUNNEL_PACKAGE_TIERS as readonly string[]).includes(value);
}

function nonEmpty(value: string | null | undefined): string | null {
  const normalized = value?.trim();

  return normalized && normalized.length > 0 ? normalized : null;
}

/**
 * Products stand in for scope breadth. Only the count of distinct offer roles is
 * used - never a product name, price, or any other free-form business input.
 */
function serviceIdsFromProducts(products: readonly Product[]): readonly string[] {
  return [...new Set(products.map((product) => product.offerRole ?? "core"))];
}

function complexitySignals(input: PackageAdviserInput): readonly PackageComplexitySignal[] {
  const fromTemplate = input.templateId ? TEMPLATE_COMPLEXITY_SIGNALS[input.templateId] : undefined;

  return fromTemplate ? [fromTemplate] : [];
}

/**
 * Decides whether the snapshot may be shown. A snapshot denominated in a
 * different currency than the visitor selected is withheld rather than
 * converted, because the planner owns no conversion authority.
 */
function resolvePricingContext(input: PackageAdviserInput): {
  readonly blockers: readonly PricingContextBlocker[];
  readonly snapshot: PlannerPricingSnapshot | null;
  readonly market: string | null;
  readonly currency: string | null;
} {
  const blockers: PricingContextBlocker[] = [];
  const market = nonEmpty(input.regionalSettings.region);
  const currency = nonEmpty(input.regionalSettings.currency);
  const snapshot = input.pricingSnapshot ?? null;

  if (!market) blockers.push("missing-region");
  if (!currency) blockers.push("missing-currency");
  if (!snapshot) {
    blockers.push("no-pricing-snapshot");
    return { blockers, snapshot: null, market, currency };
  }
  if (!currency || nonEmpty(snapshot.currency) !== currency) {
    blockers.push("currency-mismatch");
    return { blockers, snapshot: null, market, currency };
  }

  return { blockers, snapshot, market, currency };
}

/**
 * Resolves the adviser's output for the current planner context.
 *
 * `skipped` keeps the rule result visible for explanation purposes but selects
 * nothing; `overridden` honours the visitor's tier while still reporting why the
 * rule disagreed.
 */
export function resolvePlannerPackageAdvice(input: PackageAdviserInput): PlannerPackageAdvice {
  const pricingContext = resolvePricingContext(input);
  const override = isFunnelPackageTier(input.overrideTier) ? input.overrideTier : null;
  const applyOverride = input.decision === "overridden" && override !== null;

  const recommendation = resolvePackageRecommendation({
    audienceId: input.audience ?? undefined,
    serviceIds: serviceIdsFromProducts(input.products),
    complexitySignals: complexitySignals(input),
    ...(pricingContext.market ? { market: pricingContext.market } : {}),
    ...(pricingContext.currency ? { currency: pricingContext.currency } : {}),
    ...(applyOverride ? { packageOverride: override } : {}),
    ...(pricingContext.snapshot
      ? {
          pricingSnapshot: {
            version: pricingContext.snapshot.version,
            bands: pricingContext.snapshot.bands,
          },
        }
      : {}),
  });

  const manualQuoteRequired =
    recommendation.manualQuoteRequired || pricingContext.blockers.length > 0;

  const selectedTier =
    input.decision === "accepted"
      ? recommendation.tier
      : input.decision === "overridden" && override
        ? override
        : null;

  return {
    ruleVersion: recommendation.ruleVersion,
    recommendedTier: recommendation.tier,
    selectedTier,
    decision: input.decision,
    reasons: recommendation.reasons,
    // Fails closed: an incomplete context shows no band at all.
    pricingBand: manualQuoteRequired ? null : recommendation.pricingBand,
    pricingSnapshotVersion: recommendation.pricingSnapshotVersion,
    pricingCurrency: pricingContext.snapshot?.currency ?? null,
    pricingContextBlockers: pricingContext.blockers,
    missingInformation: recommendation.missingInformation,
    manualQuoteRequired,
  };
}

/** Projects the advice into the v7 session-only planner state slot. */
export function toPackageRecommendationState(
  advice: PlannerPackageAdvice,
): PlannerPackageRecommendationState {
  if (advice.decision === "none") {
    return { ...DEFAULT_PACKAGE_RECOMMENDATION };
  }

  return {
    enabled: true,
    decision: advice.decision,
    recommendedPackageId: advice.recommendedTier,
    selectedPackageId: advice.selectedTier,
    ruleVersion: advice.ruleVersion,
    pricingBand: advice.pricingBand,
    pricingSnapshotVersion: advice.pricingSnapshotVersion,
    manualQuoteRequired: advice.manualQuoteRequired,
  };
}

/** Guards against the local tier tuple drifting from the shared contract. */
export const PACKAGE_TIER_PARITY = Object.freeze({
  planner: FUNNEL_PACKAGE_TIERS,
  shared: PACKAGE_RECOMMENDATION_TIERS,
});
